class ShadeSelector extends HTMLElement {
    constructor() {
        super();
        this.swatches = this.querySelectorAll('.shade-swatch');
        this.activeNameEl = this.querySelector('.shade-studio__active-name');
        this.activeDescEl = this.querySelector('.shade-studio__active-desc');
        this.mainImageEl = this.querySelector('.shade-studio__main-image');
        this.galleryEl = this.querySelector('.shade-studio__gallery');
        this.gridEl = this.querySelector('.shade-grid');
        this.ctaEl = this.querySelector('.shade-studio__cta');
        this.imageLinkEl = this.querySelector('.shade-studio__image-link');

        this.preloadedUrls = new Set();
        this.scrollTimer = null;

        this.onSwatchClick = this.onSwatchClick.bind(this);
        this.onSwatchHover = this.onSwatchHover.bind(this);
        this.onScroll = this.onScroll.bind(this);
    }

    get isMobile() {
        return window.innerWidth < 600;
    }

    connectedCallback() {
        this.swatches.forEach(swatch => {
            swatch.addEventListener('click', this.onSwatchClick);
            swatch.addEventListener('mouseenter', this.onSwatchHover);
            swatch.addEventListener('touchstart', this.onSwatchHover, { passive: true });
        });

        if (this.gridEl) {
            this.gridEl.addEventListener('scroll', this.onScroll, { passive: true });
            /* Scroll active swatch to center on load (mobile) */
            requestAnimationFrame(() => this.scrollToActive(false));
        }
    }

    disconnectedCallback() {
        this.swatches.forEach(swatch => {
            swatch.removeEventListener('click', this.onSwatchClick);
            swatch.removeEventListener('mouseenter', this.onSwatchHover);
            swatch.removeEventListener('touchstart', this.onSwatchHover);
        });
        if (this.gridEl) {
            this.gridEl.removeEventListener('scroll', this.onScroll);
        }
    }

    scrollToActive(smooth) {
        if (!this.isMobile || !this.gridEl) return;
        var active = this.gridEl.querySelector('.shade-swatch.is-active');
        if (!active) return;
        var gridRect = this.gridEl.getBoundingClientRect();
        var swatchRect = active.getBoundingClientRect();
        var offset = (swatchRect.left + swatchRect.width / 2) - (gridRect.left + gridRect.width / 2);
        this.gridEl.scrollBy({ left: offset, behavior: smooth ? 'smooth' : 'instant' });
    }

    /* On mobile scroll, debounce and select the center swatch */
    onScroll() {
        if (!this.isMobile) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
            this.selectCenterSwatch();
        }, 100);
    }

    selectCenterSwatch() {
        if (!this.gridEl || !this.isMobile) return;
        var gridRect = this.gridEl.getBoundingClientRect();
        var centerX = gridRect.left + gridRect.width / 2;
        var closest = null;
        var closestDist = Infinity;

        this.swatches.forEach(function(swatch) {
            var rect = swatch.getBoundingClientRect();
            var dist = Math.abs((rect.left + rect.width / 2) - centerX);
            if (dist < closestDist) {
                closestDist = dist;
                closest = swatch;
            }
        });

        if (closest && !closest.classList.contains('is-active')) {
            this.activateSwatch(closest);
        }
    }

    onSwatchHover(event) {
        var swatch = event.currentTarget;
        var imageUrl = swatch.dataset.imageUrl;
        if (imageUrl && !this.preloadedUrls.has(imageUrl)) {
            this.preloadedUrls.add(imageUrl);
            var img = new Image();
            img.src = imageUrl;
        }
    }

    onSwatchClick(event) {
        var clicked = event.currentTarget;
        if (clicked.classList.contains('is-active')) return;

        this.activateSwatch(clicked);

        /* On mobile, scroll tapped swatch to center */
        if (this.isMobile && this.gridEl) {
            var gridRect = this.gridEl.getBoundingClientRect();
            var swatchRect = clicked.getBoundingClientRect();
            var offset = (swatchRect.left + swatchRect.width / 2) - (gridRect.left + gridRect.width / 2);
            this.gridEl.scrollBy({ left: offset, behavior: 'smooth' });
        }
    }

    activateSwatch(swatch) {
        this.swatches.forEach(function(s) { s.classList.remove('is-active'); });
        swatch.classList.add('is-active');

        var name = swatch.dataset.shadeName;
        var desc = swatch.dataset.shadeDesc;
        var productLink = swatch.dataset.productLink;

        if (this.activeNameEl) this.activeNameEl.textContent = name || '';
        if (this.activeDescEl) this.activeDescEl.textContent = desc || '';

        // Update CTA link
        if (this.ctaEl && productLink) {
            this.ctaEl.href = productLink;
        }

        // Update clickable image link
        if (this.imageLinkEl && productLink) {
            this.imageLinkEl.href = productLink;
        }

        // Update Image if provided
        var imageUrl = swatch.dataset.imageUrl;
        if (imageUrl && this.mainImageEl && this.mainImageEl.src !== imageUrl) {
            this.mainImageEl.classList.add('is-fading');

            var self = this;
            setTimeout(function() {
                self.mainImageEl.src = imageUrl;
                self.mainImageEl.onload = function() {
                    self.mainImageEl.classList.remove('is-fading');
                };
            }, 300);
        }
    }
}

if (!customElements.get('shade-selector')) {
    customElements.define('shade-selector', ShadeSelector);
}
